import styled from '@emotion/styled';
import { media } from '../../styles/breakpoints';
import { STAGE_ROW_HEIGHT, TOP_STAGES_MIN } from './InstanceNav.styles';

export { TOP_STAGES_MIN };

// Fixed-position to viewport — same coordinate system as the portal-mounted
// drawer rail, so they always align regardless of any wrapper layout.
// HeaderSpacer below reserves the layout slot so content doesn't slide under.
export const HEADER_HEIGHT = 60; // px — top bar: content row + padding + border

// Below this width Ylläpito's tabs go into their hamburger, leaving the top bar
// to the instance chip and the stage selector (TOP_STAGES_MIN, InstanceNav.styles).
const navCollapsed = '@media (max-width: 1499px)';

export const WideOnly = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  @media (max-width: ${TOP_STAGES_MIN - 1}px) { display: none; }
`;
export const NarrowOnly = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  @media (min-width: ${TOP_STAGES_MIN}px) { display: none; }
`;

// Holds the top bar and, on instance pages, the stage row under it.
export const HeaderShell = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;

  @media print { display: none; }
`;

// backdrop-filter makes the bar its own stacking context, so its drop-downs'
// z-index counts only inside it. Raising the bar above the stage row (which
// comes later in the DOM) keeps an open menu over the stage tabs.
export const Header = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  height: ${HEADER_HEIGHT}px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.4);
  border-bottom: 1px solid #222;
  backdrop-filter: blur(8px);

  ${media.sm} {
    padding: 12px 14px;
  }
`;

// In-flow spacer so the page content starts below the fixed header.
export const HeaderSpacer = styled.div`
  height: ${p => HEADER_HEIGHT + (p.$withStageRow ? STAGE_ROW_HEIGHT : 0)}px;
  flex-shrink: 0;
  @media print { display: none; }
`;

export const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;

  ${media.sm} {
    gap: 8px;
  }
`;

export const LogoSection = styled.div`
  display: flex;
  flex-shrink: 0;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

export const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  color: #fff;

  ${media.sm} {
    font-size: 15px;
  }
`;

export const LogoIcon = styled.span`
  font-size: 22px;
`;

export const LogoText = styled.span`
  white-space: nowrap;

  ${media.sm} {
    display: none;
  }
`;

export const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: #333;

  ${media.sm} {
    display: none;
  }
`;

// Instance zone: the instance chip and the tabs that work on it, joined in one
// box. Dimmed on server-wide pages, but still one click back into the instance.
export const InstanceZone = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 3px 4px 3px 3px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid #2a2a2a;
  border-radius: 12px;
  opacity: ${p => (p.$dimmed ? 0.5 : 1)};
  transition: opacity 0.2s ease;

  &:hover,
  &:focus-within {
    opacity: 1;
  }

  ${navCollapsed} {
    padding: 0;
    background: none;
    border: none;
    opacity: 1;
  }
`;

// Desktop row of tabs inside a zone; the drawer takes over when collapsed.
export const ZoneLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 2px;

  ${navCollapsed} {
    display: none;
  }
`;

export const ServerZone = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  ${navCollapsed} {
    display: none;
  }
`;

export const ZoneLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-right: 4px;

  /* Room for the stage selector and the longest chip: the label only fits wider */
  @media (max-width: 1599px) { display: none; }
`;

export const NavLink = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 6px 10px;
  background: ${props => props.$active ? 'rgba(0, 153, 255, 0.15)' : 'transparent'};
  border: 1px solid ${props => props.$active ? 'rgba(0, 153, 255, 0.3)' : 'transparent'};
  border-radius: 8px;
  color: ${props => props.$active ? '#0099ff' : '#888'};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 56px;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  svg {
    opacity: 0.7;
  }
`;

export const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  ${media.sm} {
    gap: 8px;
  }
`;

export const MobileNav = styled.div`
  display: none;

  ${navCollapsed} {
    display: flex;
  }
`;

export const NavHamburger = styled.button`
  display: inline-flex;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  width: ${p => (p.$compact ? 32 : 40)}px;
  height: ${p => (p.$compact ? 32 : 40)}px;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  flex-shrink: 0;

  &:hover { background: rgba(255, 255, 255, 0.08); }
`;

// Fixed under its hamburger; NavMenu sets top, left or right and max-height.
// Scrolls when the list is taller than the space below the button.
export const MobileDrawer = styled.nav`
  position: fixed;
  min-width: min(280px, calc(100vw - 28px));
  overflow-y: auto;
  overscroll-behavior: contain;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.94);
  backdrop-filter: blur(20px);
  border: 1px solid #333;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  z-index: 160;

  /* Keep rows at full height so the list scrolls instead of squashing */
  & > * { flex-shrink: 0; }
`;

export const DrawerHeading = styled.div`
  padding: 8px 14px 4px;
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:not(:first-of-type) {
    margin-top: 8px;
    border-top: 1px solid #222;
    padding-top: 12px;
  }
`;

export const DrawerLink = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 10px 14px;
  background: ${props => props.$active ? 'rgba(0, 153, 255, 0.15)' : 'transparent'};
  border: 1px solid ${props => props.$active ? 'rgba(0, 153, 255, 0.3)' : 'transparent'};
  border-radius: 8px;
  color: ${props => props.$active ? '#0099ff' : '#ddd'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.05);
    color: #fff;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  svg {
    opacity: 0.7;
  }
`;

export const NavBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 8px;
  background: ${props =>
    props.$color === 'red' ? 'rgba(239, 68, 68, 0.2)'
    : props.$color === 'yellow' ? 'rgba(250, 204, 21, 0.2)'
    : props.$color === 'green' ? 'rgba(34, 197, 94, 0.2)'
    : 'rgba(255, 255, 255, 0.1)'};
  color: ${props =>
    props.$color === 'red' ? '#f87171'
    : props.$color === 'yellow' ? '#facc15'
    : props.$color === 'green' ? '#4ade80'
    : '#888'};
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
`;
